import React, { useEffect, useState } from 'react';

import { DateTime } from 'luxon';
import axios, { type AxiosResponse } from 'axios';

import { Panel, Table, NavLinkButton } from '@shared/components/bootstrap';
import { Tooltip } from '@shared/components/bootstrap/tooltip';
import { InputFormControl } from '@shared/components/fields/input_form_control';
import { useDebounce } from '@shared/hooks';
import { useURLSearchParam } from '@shared/hooks/use_url_search_param';

import { getCustomerNames } from '@admin/components/dispatches/show/utils';
import { SelectFormControl } from '@admin/components/fields';
import { Permission } from '@admin/components/helpers/permission';
import { titleize, Titleize } from '@admin/components/helpers/titleize';
import { Spinner } from '@admin/components/spinner';
import { dispatcherURL, dispatchURL, orderURL } from '@admin/config/routes';
import { useAxiosRequest } from '@admin/hooks/use_axios_request';
import { useTitle } from '@admin/hooks/use_title';
import { ADMIN_DISPATCH } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import { useRegionsSelectorQuery } from '@admin/schema';

import { cleanupFilters, type DispatchStatus, type DispatchesFilters, isDispatchStatus, parseIdParam } from './filters';

type DispatchAssignment = {
  role: string;
  state: string;
  user: {
    email: string;
    name: string;
  };
};

type DispatchVehicle = {
  name: string;
};

type DispatchOrder = {
  id: number;
  number: number;
  type: string;
  flagged_at?: string | null;
  scheduled: string;
  address: {
    street?: string | null;
    aptsuite?: string | null;
    neighborhood?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
    business_name?: string | null;
  };
  account: {
    id: number;
    customers: { name: string }[];
  };
  region?: {
    tz: string;
  };
};

type Dispatch = {
  id: number;
  status: DispatchStatus;
  arrival: string;
  created_at: string;
  updated_at: string;
  region: {
    id: number;
    parent_id?: number | null;
    tz: string;
  };
  warehouse: {
    name: string;
  };
  depot: {
    name: string;
  };
  vehicles: DispatchVehicle[];
  assignments: DispatchAssignment[];
  orders: DispatchOrder[];
};

const DISPATCH_STATUSES: DispatchStatus[] = ['valid', 'invalid', 'canceled'];

const LOAD_ERROR_MESSAGE = 'Unable to load dispatches. Please try again.';

const REGION_FILTER_ID = 'dispatches-filter-region';
const DATE_FILTER_ID = 'dispatches-filter-date';
const SEARCH_FILTER_ID = 'dispatches-filter-search';
const STATUS_FILTER_ID = 'dispatches-filter-status';

const assignmentStateClass = (state: string): string => {
  switch (state) {
    case 'lead':
      return 'label-primary';
    case 'accepted':
      return 'label-success';
    case 'rejected':
      return 'label-warning';
    case 'canceled':
      return 'label-danger';
    case 'completed':
      return 'label-info';
    default:
      return 'label-default';
  }
};

const dispatchStatusLabel = (status: DispatchStatus) => {
  switch (status) {
    case 'valid':
      return <span className="label label-success">Valid</span>;
    case 'invalid':
      return <span className="label label-danger">Invalid</span>;
    case 'canceled':
      return <span className="label label-warning">Canceled</span>;
    default:
      return null;
  }
};

const formatArrival = (arrival: string, tz: string) =>
  DateTime.fromISO(arrival).setZone(tz).toFormat('MMM d, yyyy h:mm:ss a');

const formatDispatcherDate = (arrival: string, tz: string) =>
  DateTime.fromISO(arrival).setZone(tz).toFormat('yyyy-MM-dd');

const checkOrderInvalid = (dispatch: Dispatch, order: DispatchOrder) => {
  if (dispatch.status === 'valid' || !order.flagged_at) {
    return false;
  }

  const createdAt = Date.parse(dispatch.created_at);
  const updatedAt = Date.parse(dispatch.updated_at);
  const flaggedAt = Date.parse(order.flagged_at);

  return flaggedAt > createdAt && flaggedAt > updatedAt;
};

const buildOrderTooltip = (order: DispatchOrder) => {
  const tz = order.region?.tz ?? 'America/Los_Angeles';
  const scheduled = DateTime.fromISO(order.scheduled).setZone(tz).toLocaleString(DateTime.DATETIME_MED);
  const customers = getCustomerNames(order.account.customers);
  const address = order.address;
  const addressLine = [address.street, address.aptsuite].filter(Boolean).join(' ');
  const locality = address.neighborhood ? `${address.city} (${address.neighborhood})` : address.city;
  const addressSuffix = [locality, address.state, address.zip].filter(Boolean).join(', ');

  return [customers, scheduled, addressLine, address.business_name, addressSuffix].filter(Boolean).join('<br />');
};

const useDispatchesRequest = ({ region_id, date, status, query }: DispatchesFilters) => {
  const [request, setRequest] = useState<Promise<AxiosResponse<Dispatch[]>> | undefined>();

  useEffect(() => {
    setRequest(
      axios.get<Dispatch[]>('/dispatches.json', {
        params: cleanupFilters({ region_id, date, status, query }),
      }),
    );
  }, [region_id, date, status, query]);

  return useAxiosRequest(request);
};

export const DispatchesList: React.FC = () => {
  useTitle('Dispatches');
  const { data: regionData, loading: regionsLoading } = useRegionsSelectorQuery({ client });
  const regions = regionData?.regions ?? [];
  const [regionIDParam, setRegionIDParam] = useURLSearchParam('region_id');
  const [date, setDate] = useURLSearchParam('date');
  const [query, setQuery] = useURLSearchParam('query');
  const [statusParam, setStatusParam] = useURLSearchParam('status');
  const region_id = parseIdParam(regionIDParam ?? null);
  const status = statusParam && isDispatchStatus(statusParam) ? statusParam : undefined;
  const debouncedQuery = useDebounce(query);
  const requestQuery = query ? debouncedQuery : undefined;
  const { loading, response, error } = useDispatchesRequest({
    region_id,
    date,
    status,
    query: requestQuery,
  });
  const dispatches = response?.data;
  const loadError = error ? LOAD_ERROR_MESSAGE : undefined;

  return (
    <>
      <Panel>
        <Panel.Body>
          <form className="form-horizontal">
            <div className="form-group">
              <label className="col-sm-3 control-label" htmlFor={REGION_FILTER_ID}>
                Region:
              </label>
              <div className="col-sm-9">
                <SelectFormControl
                  id={REGION_FILTER_ID}
                  prompt="All"
                  options={regions.map((region) => String(region.id))}
                  value={regionIDParam ?? ''}
                  disabled={regionsLoading}
                  name={(regionID) => regions.find((region) => String(region.id) === regionID)?.name ?? regionID}
                  onChange={setRegionIDParam}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="col-sm-3 control-label" htmlFor={DATE_FILTER_ID}>
                Date:
              </label>
              <div className="col-sm-9">
                <InputFormControl
                  id={DATE_FILTER_ID}
                  type="date"
                  value={date ?? ''}
                  onChange={(event) => setDate(event.target.value || undefined)}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="col-sm-3 control-label" htmlFor={SEARCH_FILTER_ID}>
                Search:
              </label>
              <div className="col-sm-9">
                <InputFormControl
                  id={SEARCH_FILTER_ID}
                  type="text"
                  value={query ?? ''}
                  onChange={(event) => setQuery(event.target.value || undefined)}
                  placeholder="Search by name, email, phone, account # or order #"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="col-sm-3 control-label" htmlFor={STATUS_FILTER_ID}>
                Status:
              </label>
              <div className="col-sm-9">
                <SelectFormControl
                  id={STATUS_FILTER_ID}
                  prompt="All"
                  options={DISPATCH_STATUSES}
                  value={status ?? ''}
                  name={titleize}
                  onChange={setStatusParam}
                />
              </div>
            </div>
          </form>
        </Panel.Body>
      </Panel>

      <Panel>
        <Panel.Body>
          {loadError && (
            <div className="alert alert-danger" role="alert">
              {loadError}
            </div>
          )}
          {!loadError && dispatches && dispatches.length === 0 && (
            <div className="text-center">No dispatches found matching criteria.</div>
          )}
          {!loadError && dispatches && dispatches.length > 0 && (
            <Table responsive striped>
              <thead>
                <tr>
                  <th className="col-sm-1">ID</th>
                  <th className="col-sm-4">Details</th>
                  <th className="col-sm-3">Assignments</th>
                  <th className="col-sm-3">Orders</th>
                  <th className="col-sm-1">Status</th>
                  <th className="col-zero col-nowrap text-right" />
                </tr>
              </thead>
              <tbody>
                {dispatches?.map((dispatch) => (
                  <tr key={dispatch.id}>
                    <td className="col-sm-1">{dispatch.id}</td>
                    <td className="col-sm-4">
                      <div>{formatArrival(dispatch.arrival, dispatch.region.tz)}</div>
                      <div>{dispatch.warehouse.name}</div>
                      <div>
                        {dispatch.vehicles.map((vehicle, index) => (
                          <div key={`${dispatch.id}-vehicle-${index}`}>{vehicle.name}</div>
                        ))}
                      </div>
                      <div>{dispatch.depot.name}</div>
                    </td>
                    <td className="col-sm-3">
                      {dispatch.assignments.map((assignment) => (
                        <div
                          key={`${dispatch.id}-assignment-${assignment.user.email}-${assignment.role}`}
                          className="pad-btm"
                        >
                          <a href={`mailto:${assignment.user.email}`}>{assignment.user.name}</a>
                          <span> ({assignment.role})</span>
                          <span className={`label ${assignmentStateClass(assignment.state)}`}>
                            <Titleize text={assignment.state} />
                          </span>
                        </div>
                      ))}
                    </td>
                    <td className="col-sm-3">
                      {dispatch.orders.map((order) => {
                        const invalid = checkOrderInvalid(dispatch, order);
                        const tooltip = buildOrderTooltip(order);

                        return (
                          <Tooltip key={order.id} title={tooltip}>
                            <div className={invalid ? 'text-danger' : undefined}>
                              <a
                                href={orderURL({ id: order.id, account: { id: order.account.id } })}
                                className={invalid ? 'text-danger' : undefined}
                              >
                                #{order.number} (<Titleize text={order.type} />)
                              </a>
                            </div>
                          </Tooltip>
                        );
                      })}
                    </td>
                    <td className="col-sm-1">{dispatchStatusLabel(dispatch.status)}</td>
                    <td className="col-zero col-nowrap text-right">
                      <Permission resource={ADMIN_DISPATCH.resource} action={ADMIN_DISPATCH.actions.read} denied={null}>
                        <NavLinkButton
                          kind="primary"
                          to={dispatcherURL({
                            date: formatDispatcherDate(dispatch.arrival, dispatch.region.tz),
                            regionID: String(dispatch.region.parent_id || dispatch.region.id),
                          })}
                        >
                          View in Dispatcher
                        </NavLinkButton>
                      </Permission>
                      <NavLinkButton kind="info" to={dispatchURL({ id: dispatch.id })}>
                        View
                      </NavLinkButton>
                      <NavLinkButton kind="info" to={`/dispatches/${dispatch.id}/vehicle_flags`} target="_blank">
                        Print
                      </NavLinkButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          {loading && !loadError && <Spinner />}
        </Panel.Body>
      </Panel>
    </>
  );
};
